'use client';

import React, { useState } from 'react';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageValue, NoMessageValues } from 'react-intl';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { z } from 'zod';
import { useForgotPasswordRequestEmailGql } from '../api/useForgotPasswordRequestEmailGql';
import { ForgottenPasswordSuccess } from './ForgottenPasswordSuccess.client';

const iconStylingTailwind = 'w-4 h-4 md:w-3 md:h-3 text-color-text-dark';

const messages = defineMessages<{
  readonly emailLabel: NoMessageValues;
  readonly submitLabel: NoMessageValues;
  readonly invalidToken: NoMessageValues;
  readonly headline: { readonly break: MessageValue };
  readonly description: NoMessageValues;
  readonly requiredFields: NoMessageValues;
  readonly generalError: NoMessageValues;
  readonly close: NoMessageValues;
}>({
  emailLabel: {
    id: 'ForgotPasswordForm.label.email',
    defaultMessage: 'Hier bitte die E-Mail-Adresse eintragen: *',
  },
  submitLabel: {
    id: 'ForgotPasswordForm.label.submit',
    defaultMessage: 'Absenden!',
  },
  invalidToken: {
    id: 'ForgotPasswordForm.invalidToken',
    defaultMessage:
      'Bitte beachten Sie: Ihre eingegebene URL ist nicht mehr gültig. Bitte fordern Sie einen neuen Link an.',
  },
  headline: {
    id: 'ForgotPasswordForm.headline',
    defaultMessage: 'Keine Sorge! {break} Wir helfen Ihnen, sich wieder anzumelden.',
  },
  description: {
    id: 'ForgotPasswordForm.description',
    defaultMessage:
      'Geben Sie hier Ihre E-Mail-Adresse ein. Wir senden Ihnen dann eine Nachricht mit weiteren Anweisungen zum Zurücksetzen Ihres Passworts zu.',
  },
  requiredFields: {
    id: 'Login.requiredFieldsHint',
    defaultMessage: '* Diese Felder müssen ausgefüllt werden',
  },
  generalError: {
    id: 'ForgotPasswordModal.request.generalError',
    defaultMessage:
      'Wir konnten Ihre Anfrage leider nicht bearbeiten. Versuchen Sie es später erneut.',
  },
  close: {
    id: 'ForgotPasswordSuccess.close',
    defaultMessage: 'Fenster schließen',
  },
});

export const UserSchema = z.object({
  email: z.string().email('Please correct your e-mail'),
});

export interface ForgotPasswordFormProps {
  onClose: () => void;
}

export const ForgottenPasswordForm = ({ onClose }: ForgotPasswordFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>({ resolver: zodResolver(UserSchema), mode: 'onChange' });
  const { formatMessage } = useIntl();
  const { error, loading, passwordForget } = useForgotPasswordRequestEmailGql();
  const [isSuccess, setIsSuccess] = useState(false);

  const submit = async (data: { email: string }) => {
    setIsSuccess(false);
    const success = await passwordForget(data.email);
    setIsSuccess(success);
  };

  return (
    <>
      {error && <Notification severity="error">{error}</Notification>}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#fff]/50">
          <CircularProgress className={iconStylingTailwind} />
        </div>
      )}
      {!loading &&
        (isSuccess ? (
          <div className="flex flex-col">
            <ForgottenPasswordSuccess />
            <div className="bg-color-background-base flex w-full flex-col items-stretch p-2 lg:items-end">
              <Button className="w-full lg:w-fit" color="primary" onClick={onClose}>
                {formatMessage(messages.close)}
              </Button>
            </div>
          </div>
        ) : (
          <form className="bg-color-grey-light h-full" onSubmit={handleSubmit(submit)}>
            <div className="flex flex-col gap-2 px-2 py-4">
              <div className="text-size-body-sm font-bold">
                {formatMessage(messages.headline, {
                  break: <br />,
                })}
              </div>
              <div className="text-size-body-sm font-medium">
                {formatMessage(messages.description)}
              </div>

              <div className="text-size-body-sm text-left">
                {formatMessage(messages.requiredFields)}
              </div>

              <Input
                label={formatMessage(messages.emailLabel)}
                type="email"
                error={!!errors?.email}
                helperText={errors?.email ? errors.email?.message : undefined}
                {...register('email')}
              />
            </div>
            <div className="bg-color-background-base flex w-full justify-center p-4">
              <Button className="px-6" type="submit">
                {formatMessage(messages.submitLabel)}
              </Button>
            </div>
          </form>
        ))}
    </>
  );
};
